import {
  Component,
  computed,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  QueryList,
  signal,
  ViewChildren,
  WritableSignal,
} from '@angular/core';
import { ConfigInput } from '../../interfaces/interfaces';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { Title } from '@angular/platform-browser';
import { SecurityService } from '../../services/security.service';
import { CommonModule } from '@angular/common';
import { InputComponent } from '../../shared/input/input.component';
import { LoaderService } from '../../services/loader.service';
import { HelpService } from '../../services/help.service';
import { RouteService } from 'app/services/route.service';
import { toast } from 'ngx-sonner';
import { buildPreloadedConfig, normalizeFieldValue } from '../../shared/utils/field-preload.utils';

@Component({
  selector: 'app-balance',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    FormsModule,
    InputComponent
  ],
  templateUrl: './balance.component.html',
  styleUrl: './balance.component.css'
})
export class BalanceComponent implements OnInit, OnDestroy {
  @ViewChildren(InputComponent) inputComponents!: QueryList<InputComponent>;

  form: FormGroup;
  incomeExtraActivity: number = 0;
  nextRoute: WritableSignal<string> = signal('');
  isSubmitting = signal(false);
  canContinue = signal(false);
  validationMessage = signal('');
  isLoadingData = signal(true);
  isLightVersion = signal(false);
  private formSubscription: any;

  dataInputs: WritableSignal<ConfigInput[]> = signal([
    { label: 'Egresos', subLabel: '', formName: 'outcome', placeholder: '', type: 'number', defaultValue: '', enabled: true, validation: [Validators.required, Validators.max(999999999)], textError: 'Por favor ingresa un valor válido', showBlocker: true },
    { label: 'Activos', subLabel: '', formName: 'assets', placeholder: '', type: 'number', defaultValue: '', enabled: true, validation: [Validators.required, Validators.min(400000), Validators.max(999999999)], textError: 'Por favor ingresa un valor válido', showBlocker: true },
    { label: 'Pasivos', subLabel: '', formName: 'liabilities', placeholder: '', type: 'number', defaultValue: '', enabled: true, validation: [Validators.required, Validators.min(100000), Validators.max(999999999)], textError: 'Por favor ingresa un valor válido', showBlocker: true },
  ]);

  visibleDataInputs = computed(() => {
    if (!this.isLightVersion()) {
      return this.dataInputs();
    }
    return this.dataInputs().filter((input) =>
      input.formName === 'assets' || input.formName === 'liabilities'
    );
  });

  @Output() validData = new EventEmitter<boolean>();
  @Input() isCheckData = false;
  @Input() dataLastOnboarding: any;

  constructor(
    private _router: Router,
    private _title: Title,
    private _securityService: SecurityService,
    private _loaderService: LoaderService,
    private _helpService: HelpService,
    private _routeService: RouteService
  ) {
    this._title.setTitle('Onboarding Digital | Balance');
    this.form = new FormGroup({
      outcome: new FormControl(null, [Validators.required, Validators.max(999999999)]),
      assets: new FormControl(null, [Validators.required, Validators.min(400000), Validators.max(999999999)]),
      liabilities: new FormControl(null, [Validators.required, Validators.min(100000), Validators.max(999999999)]),
    });
    this.nextRoute.set(this._routeService.getNextRoute(this._router.url) ?? '');

    const lightVersion = localStorage.getItem('lightVersion');
    this.isLightVersion.set(lightVersion === 'true');
  }

  async ngOnInit() {
    const authRoute = localStorage.getItem('route');
    if (!authRoute) {
      this._router.navigateByUrl('');
      return;
    }
    const currentUrl = this._router.url;
    if (authRoute && currentUrl !== authRoute) {
      await this._router.navigateByUrl(authRoute);
    } else {
      this.getData();
      this._loaderService.hide();
      this._helpService.show();
    }
  }

  getData() {
    this.isLoadingData.set(true);
    this._securityService.getBalance().subscribe({
      next: (response: any) => {
        this.form.patchValue({
          outcome: normalizeFieldValue(response.outcome, 'number') || null,
          assets: normalizeFieldValue(response.assets, 'number') || null,
          liabilities: normalizeFieldValue(response.liabilities, 'number') || null,
        }, { emitEvent: false });

        this.dataInputs.update((inputs: any) =>
          inputs.map((input: any) => buildPreloadedConfig(input, response[input.formName], input.type))
        );

        this.incomeExtraActivity = response.incomeExtraActivity ? response.incomeExtraActivity : 0;
        this.isLoadingData.set(false);
        this.updateCanContinue();
        this.setupFormSubscription();
      },
      error: (error: any) => {
        console.error('ERROR: ', error);
        this.isLoadingData.set(false);
        toast.error('No pudimos cargar tus datos. Puedes ingresarlos manualmente.');
      }
    });
  }

  onFieldStateChange(): void {
    this.updateCanContinue();
  }

  handleValue(event: { formName: string, value: string | number | null }) {
    const parsed = this.parseFieldValue(event.value);
    const control = this.form.get(event.formName);
    control?.setValue(parsed);
    control?.markAsTouched();
    control?.updateValueAndValidity();
    this.updateCanContinue();

    if (this.validationMessage()) {
      this.validationMessage.set('');
    }
  }

  onSubmit(): Promise<void> | void {
    if (this.isCheckData) {
      return this.saveBalanceData();
    }

    this.validationMessage.set('');
    this.syncFromInputs();

    if (!this.form.valid || !this.areAllFieldsFilledAndValid()) {
      this.validationMessage.set('Revisa que todos los campos tengan valores válidos antes de continuar.');
      this.showInputValidationErrors();
      toast.error('Revisa los valores ingresados antes de continuar.');
      return;
    }

    return this.saveBalanceData().then(() => {
      localStorage.setItem('route', this.nextRoute());
      this._router.navigateByUrl(this.nextRoute());
    });
  }

  saveBalanceData(): Promise<void> {
    this.syncFromInputs();
    this.isSubmitting.set(true);

    const payload = {
      outcome: this.toNumber(this.form.get('outcome')?.value),
      assets: this.toNumber(this.form.get('assets')?.value),
      liabilities: this.toNumber(this.form.get('liabilities')?.value),
      incomeExtraActivity: this.incomeExtraActivity,
    };

    return new Promise((resolve, reject) => {
      this._securityService.setBalance(payload).subscribe({
        next: () => {
          this.isSubmitting.set(false);
          resolve();
        },
        error: (error: any) => {
          this.isSubmitting.set(false);
          console.error('ERROR: ', error);
          toast.error('No pudimos guardar tu información. Verifica tu conexión e intenta de nuevo.');
          reject(error);
        }
      });
    });
  }

  private isFormCompletelyValid(): boolean {
    return this.areAllFieldsFilledAndValid();
  }

  private emitValidationState(): void {
    if (!this.isCheckData) return;
    this.validData.emit(this.isFormCompletelyValid());
  }

  private setupFormSubscription(): void {
    if (!this.isCheckData) return;

    if (this.formSubscription) {
      this.formSubscription.unsubscribe();
    }

    this.formSubscription = this.form.valueChanges.subscribe(() => {
      this.emitValidationState();
    });

    setTimeout(() => this.emitValidationState(), 500);
  }

  private updateCanContinue(): void {
    this.syncFromInputs();
    this.canContinue.set(this.areAllFieldsFilledAndValid());
    this.emitValidationState();
  }

  private areAllFieldsFilledAndValid(): boolean {
    const requiredFields = this.visibleDataInputs().map((input) => input.formName);

    return requiredFields.every((field) => {
      const control = this.form.get(field ?? '');
      const value = control?.value;
      return value !== null &&
        value !== undefined &&
        value !== '' &&
        !isNaN(parseFloat(value)) &&
        !!control?.valid;
    });
  }

  private syncFromInputs(): void {
    this.inputComponents?.forEach((input) => {
      const { formName, value } = input.getCurrentValue();
      if (formName) {
        this.form.get(formName)?.setValue(this.parseFieldValue(value), { emitEvent: false });
      }
    });
    this.form.updateValueAndValidity({ emitEvent: false });
  }

  private showInputValidationErrors(): void {
    this.inputComponents?.forEach((input) => {
      if (!input.getCurrentValue().valid) {
        input.forceShowValidation();
      }
    });
  }

  private parseFieldValue(value: string | number | null | undefined): number | null {
    if (value === null || value === undefined || value === '') {
      return null;
    }

    const normalized = typeof value === 'number'
      ? value
      : parseFloat(String(value).replace(/,/g, ''));

    return Number.isNaN(normalized) ? null : normalized;
  }

  private toNumber(value: unknown): number {
    const parsed = this.parseFieldValue(value as string | number | null);
    return parsed ?? 0;
  }

  getGains(): string {
    const outcome = parseFloat(this.form.get('outcome')?.value);
    const assets = parseFloat(this.form.get('assets')?.value);
    const liabilities = parseFloat(this.form.get('liabilities')?.value);

    if (isNaN(this.incomeExtraActivity) || isNaN(outcome) || isNaN(assets) || isNaN(liabilities)) {
      throw new Error('Todos los campos deben ser números válidos.');
    }

    const gains = (this.incomeExtraActivity - outcome) + (assets - liabilities);
    return gains.toString();
  }

  ngOnDestroy() {
    if (this.formSubscription) {
      this.formSubscription.unsubscribe();
    }
  }
}
